import React, { createContext, ReactNode, useContext, useMemo } from "react"
import { PushSubscriptionInfo, registerAndSubscribe } from "utils/pushNotificationUtils"
import { useSubscribePushNotificationMutation } from "appRedux/slices/notificationApiSlice"
import { useAppSelector } from "appRedux/hooks"
import useDeviceId from "hooks/useDeviceId"

interface NotificationContextType {
  handleSubscribe: () => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const PushNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const vapidPublicKey = useAppSelector((state) => state.initSlice.vapidPublicKey)

  const deviceId = useDeviceId()

  const [triggerSubscribePushNotification] = useSubscribePushNotificationMutation()

  const handleSubscribe = (): Promise<boolean> => {
    if (!deviceId) {
      console.warn("deviceId not exist")
      return Promise.resolve(false)
    }

    const onSubscribe = (subscriptionInfo: PushSubscriptionInfo) => {
      const subscriptionData = {
        deviceId: deviceId,
        endpoint: subscriptionInfo.endpoint,
        publicKey: subscriptionInfo.publicKey,
        auth: subscriptionInfo.auth
      }
      triggerSubscribePushNotification(subscriptionData)
    }

    const onError = (e: Error) => {
      console.error("Failed to subscribe. Error: ", e)
    }

    return registerAndSubscribe(onSubscribe, onError, vapidPublicKey)
  }

  const contextValue = useMemo(
    () => ({
      handleSubscribe
    }), [vapidPublicKey, deviceId]
  )

  return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
}

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error("useNotification must be used within a NotificationProvider")
  }
  return context
}
